import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, AlertCircle, CheckCircle2, Eye, EyeOff } from 'lucide-react';
import AuthShell from '../components/AuthShell';
import { authApi, formatApiErrorDetail } from '../lib/authApi';

function passwordStrength(p) {
  let n = 0;
  if (!p) return 0;
  if (p.length >= 8) n++;
  if (/[A-Z]/.test(p)) n++;
  if (/[a-z]/.test(p)) n++;
  if (/\d/.test(p)) n++;
  if (/[^A-Za-z0-9]/.test(p)) n++;
  return Math.min(n, 4);
}

export default function SignupPage() {
  const [params] = useSearchParams();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [inviteCode, setInviteCode] = useState(params.get('invite') || '');
  const [terms, setTerms] = useState(false);
  const [show, setShow] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const strength = useMemo(() => passwordStrength(password), [password]);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password !== confirm) { setError('Passwords do not match'); return; }
    if (strength < 2) { setError('Please choose a stronger password (8+ chars, mix of letters / numbers).'); return; }
    if (!terms) { setError('You must accept the terms to continue.'); return; }
    setLoading(true);
    try {
      await authApi.register({
        email, password, first_name: firstName, last_name: lastName,
        company: company || undefined, phone: phone || undefined,
        invite_code: inviteCode, accept_terms: true,
      });
      setSuccess(true);
      // GA conversion event: a new user just completed signup.
      try { window.gtag && window.gtag('event', 'sign_up', { method: 'email' }); } catch (_) {}
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <AuthShell title="Check your inbox soon" subtitle="Account created">
        <div className="rounded-md bg-[#E5F0EA] border border-[#4A7C5E]/40 p-4 text-[13px] text-[#2D5F4F]" data-testid="signup-success">
          <div className="flex items-start gap-2.5">
            <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold">Welcome aboard.</div>
              <p className="mt-1.5 leading-relaxed">
                Your request has been received. An administrator will review and approve your access shortly. You will be able to sign in once approved.
              </p>
            </div>
          </div>
        </div>
        <button onClick={() => navigate('/login')} className="mt-6 w-full h-11 rounded-md bg-[#1A2622] text-[#FBF6EC] text-[14px] font-medium hover:bg-[#2D5F4F]">
          Back to sign in
        </button>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Create your account" subtitle="Join the platform">
      <form onSubmit={submit} className="space-y-3.5" data-testid="signup-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="First name" required value={firstName} onChange={setFirstName} testId="signup-first" autoComplete="given-name" />
          <Field label="Last name" required value={lastName} onChange={setLastName} testId="signup-last" autoComplete="family-name" />
        </div>
        <Field label="Company / facility" value={company} onChange={setCompany} testId="signup-company" autoComplete="organization" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Email" required type="email" value={email} onChange={setEmail} testId="signup-email" autoComplete="email" />
          <Field label="Phone (optional)" type="tel" value={phone} onChange={setPhone} testId="signup-phone" autoComplete="tel" />
        </div>
        <div>
          <Field label="Password" required type={show ? 'text' : 'password'} value={password} onChange={setPassword} testId="signup-password" autoComplete="new-password"
            rightSlot={
              <button type="button" onClick={() => setShow((s) => !s)} className="text-[#7A857F] hover:text-[#1A2622]" aria-label="Toggle">{show ? <EyeOff size={15} /> : <Eye size={15} />}</button>
            } />
          <div className="mt-1.5 flex items-center gap-1.5">
            {[0,1,2,3].map((i) => (
              <span key={i} className={`h-1.5 flex-1 rounded-full ${strength > i ? (strength >= 3 ? 'bg-[#4A7C5E]' : 'bg-[#A06628]') : 'bg-[#E5DCC4]'}`} />
            ))}
            <span className="text-[10.5px] text-[#7A857F] w-14 text-right">{['Weak','Fair','Good','Strong','Strong'][strength]}</span>
          </div>
        </div>
        <Field label="Confirm password" required type={show ? 'text' : 'password'} value={confirm} onChange={setConfirm} testId="signup-confirm" autoComplete="new-password" />
        <Field label="Invite code" required value={inviteCode} onChange={(v) => setInviteCode(v.toUpperCase())} testId="signup-invite" placeholder="REQUIRED — provided by your admin" />

        <label className="flex items-start gap-2.5 text-[12.5px] text-[#3A4A44] mt-1">
          <input type="checkbox" required checked={terms} onChange={(e) => setTerms(e.target.checked)} className="mt-0.5 w-4 h-4 rounded border-[#D4C9AF] text-[#4A7C5E] focus:ring-[#4A7C5E]" data-testid="signup-terms" />
          <span>I agree to the terms of service and acknowledge that this platform contains public CMS / NPPES data only.</span>
        </label>

        {error && (
          <div role="alert" className="flex items-start gap-2 text-[12.5px] text-[#7A2E0F] bg-[#FBE3D6] border border-[#E8B79A] rounded-md px-3 py-2.5" data-testid="signup-error">
            <AlertCircle size={14} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <button
          type="submit" disabled={loading}
          className="w-full h-11 mt-1 inline-flex items-center justify-center gap-1.5 rounded-md bg-[#1A2622] text-[#FBF6EC] text-[14px] font-medium hover:bg-[#2D5F4F] disabled:opacity-60"
          data-testid="signup-submit"
        >
          {loading ? 'Creating…' : (<>Create account <ArrowRight size={14} /></>)}
        </button>

        <p className="text-[13px] text-[#3A4A44] pt-2">
          Already have an account?{' '}
          <Link to="/login" className="text-[#4A7C5E] hover:text-[#2D5F4F] font-medium">Sign in</Link>
        </p>
      </form>
    </AuthShell>
  );
}

function Field({ label, value, onChange, type = 'text', required, testId, autoComplete, placeholder, rightSlot }) {
  return (
    <label className="block">
      <span className="text-[11.5px] font-semibold uppercase tracking-[0.14em] text-[#3A4A44]">{label}{required && <span className="text-[#A06628]"> *</span>}</span>
      <div className="mt-1.5 relative">
        <input
          type={type} required={required} autoComplete={autoComplete}
          value={value} onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full h-11 px-3.5 pr-10 rounded-md bg-white border border-[#E5DCC4] text-[14px] text-[#1A2622] placeholder:text-[#A8B0AA] focus:border-[#4A7C5E] outline-none transition-colors"
          data-testid={testId}
        />
        {rightSlot && <div className="absolute right-3 top-1/2 -translate-y-1/2">{rightSlot}</div>}
      </div>
    </label>
  );
}
